import React, { useState } from 'react';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import { Card } from '../../components/ui/Card';
import { MasterDataTable } from '../../components/admin/MasterDataTable';
import { MasterDataForm } from '../../components/admin/MasterDataForm';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Category } from '../../types/masterData';

export const CategoriesPage: React.FC = () => {
  const { categories, subcategories, isLoading, createCategory, updateCategory, deleteCategory, createSubcategory, updateSubcategory, deleteSubcategory } = useAdminConfig();
  const [subTab, setSubTab] = useState<'categories' | 'subcategories'>('categories');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);

  if (isLoading) return <LoadingSpinner size="lg" text="Loading Categories..." />;

  const categoryFields = [
    { key: 'name', label: 'Category Name', required: true },
    { key: 'code', label: 'Category Code', required: true },
  ];
  const subcategoryFields = [
    { key: 'name', label: 'Subcategory Name', required: true },
    { key: 'category_id', label: 'Parent Category', type: 'select' as const, options: categories.map(c => ({ value: c.category_id, label: c.name })), required: true },
  ];

  const handleSaveCategory = async (data: Record<string, string>) => {
    if (editing) await updateCategory(editing.category_id, data);
    else await createCategory(data);
    setFormOpen(false);
  };

  const handleSaveSubcategory = async (data: Record<string, string>) => {
    if (editing) await updateSubcategory(editing.category_id, data);
    else await createSubcategory(data);
    setFormOpen(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-slate-100">Categories & Subcategories</h3>
        <p className="text-xs text-slate-400">Manage work classification taxonomy</p>
      </div>

      <div className="flex border-b border-slate-800 gap-1">
        <button onClick={() => setSubTab('categories')}
          className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors ${subTab === 'categories' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'}`}>
          Categories ({categories.length})
        </button>
        <button onClick={() => setSubTab('subcategories')}
          className={`px-4 py-2.5 text-xs font-semibold rounded-t-lg transition-colors ${subTab === 'subcategories' ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400' : 'text-slate-400 hover:text-slate-200'}`}>
          Subcategories ({subcategories.length})
        </button>
      </div>

      <Card>
        {subTab === 'categories' ? (
          <MasterDataTable<Record<string, unknown>>
            data={categories.map(c => ({ ...c }))}
            columns={[
              { key: 'name', label: 'Category Name', render: item => <span className="font-semibold text-slate-100">{String(item.name)}</span> },
              { key: 'code', label: 'Code', render: item => <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded font-mono">{String(item.code)}</span> },
              { key: 'category_id', label: 'ID', render: item => <span className="text-[10px] font-mono text-slate-500">{String(item.category_id)}</span> },
            ]}
            onAdd={() => { setEditing(null); setFormOpen(true); }}
            onEdit={item => { setEditing({ category_id: String(item.category_id), name: String(item.name), code: String(item.code) } as Category); setFormOpen(true); }}
            onDelete={item => deleteCategory(String(item.category_id))}
            searchKey="name" searchPlaceholder="Search categories..."
          />
        ) : (
          <MasterDataTable<Record<string, unknown>>
            data={subcategories.map(s => ({ ...s }))}
            columns={[
              { key: 'name', label: 'Subcategory Name', render: item => <span className="font-semibold text-slate-100">{String(item.name)}</span> },
              { key: 'category_id', label: 'Parent', render: item => <span className="text-slate-300">{categories.find(c => c.category_id === String(item.category_id))?.name || String(item.category_id)}</span> },
              { key: 'subcategory_id', label: 'ID', render: item => <span className="text-[10px] font-mono text-slate-500">{String(item.subcategory_id)}</span> },
            ]}
            onAdd={() => { setEditing(null); setFormOpen(true); }}
            onEdit={item => { setEditing({ category_id: String(item.category_id), name: String(item.name), code: '' } as Category); setFormOpen(true); }}
            onDelete={item => deleteSubcategory(String(item.subcategory_id))}
            searchKey="name" searchPlaceholder="Search subcategories..."
          />
        )}
      </Card>

      <MasterDataForm isOpen={formOpen} onClose={() => setFormOpen(false)}
        onSave={subTab === 'categories' ? handleSaveCategory : handleSaveSubcategory}
        title={editing ? `Edit ${subTab === 'categories' ? 'Category' : 'Subcategory'}` : `Add New ${subTab === 'categories' ? 'Category' : 'Subcategory'}`}
        fields={subTab === 'categories' ? categoryFields : subcategoryFields}
        initialData={editing ? { name: editing.name, code: (editing as Category).code || '', category_id: (editing as unknown as { category_id?: string }).category_id || '' } : undefined} />
    </div>
  );
};
